'use client';
import { NotificationSkeleton } from '@/components/notification-skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useNotifications } from '@/hooks/useNotifications';
import { cn, getRootLink } from '@/lib/utils';
import { NotificationData } from '@/types/api/notification';
import { QuestionLinkType } from '@/types/api/question';
import { differenceInWeeks, format, formatDistanceToNow } from 'date-fns';
import { Ellipsis } from 'lucide-react';
import Link from 'next/link';
import { InfiniteScrollTrigger } from './infinite-scroll-trigger';

interface NotificationContainerProps {
  notifications: NotificationData[];
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
}

export const NotificationContainer = ({
  notifications,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  isLoading,
}: NotificationContainerProps) => {
  const { setTargetRef } = useInfiniteScroll({
    onIntersect: () => {
      if (fetchNextPage && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    enabled: hasNextPage && !!fetchNextPage && !isFetchingNextPage,
  });

  return (
    <div className="flex flex-col divide-y rounded-l py-4">
      {isLoading && notifications.length === 0 ? (
        <NotificationSkeleton />
      ) : notifications.length === 0 ? (
        <div className="flex items-center justify-center p-20">
          <p className="text-sm text-gray-500">No notifications found.</p>
        </div>
      ) : (
        notifications.map((notification, index) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            isFirst={index === 0}
            isLast={index === notifications.length - 1}
          />
        ))
      )}
      {hasNextPage && (
        <InfiniteScrollTrigger
          isLoading={isFetchingNextPage}
          hasNextPage={hasNextPage}
          setTargetRef={setTargetRef}
        />
      )}
    </div>
  );
};

const NotificationItem = ({
  notification,
  isFirst,
  isLast,
}: {
  notification: NotificationData;
  isFirst: boolean;
  isLast: boolean;
}) => {
  const isRead = notification.isRead;
  const createdAtDate = new Date(notification.createdDate);
  const weeksDifference = differenceInWeeks(new Date(), createdAtDate);
  const { changeNotificationReadStatus } = useNotifications({});

  const getNotificationText = (): string => {
    /*
   - 'user' commented on your story 'story title'
   - 'user' mentioned you in a comment on 'story title'
   - 'user' replied to your comment on 'story title'
  */

    if (notification.notificationType === 'COMMENT') {
      if (notification.parentCommentId) {
        return 'replied to your comment on';
      }
      return 'commented on your story';
    } else if (notification.notificationType === 'MENTION') {
      return 'mentioned you in a comment on';
    }

    return '';
  };

  const handleChangeReadStatus = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    changeNotificationReadStatus(notification.id, !isRead);
  };
  const markAsRead = () => {
    changeNotificationReadStatus(notification.id, true);
  };

  const getNotificationLink = (): string => {
    const root = getRootLink(
      notification.question.questionType as QuestionLinkType
    );
    const link = `/${root}/question/${notification.questionSlug}?focusedStoryId=${notification.story.id}&focusedCommentId=${notification.commentId}`;

    return link;
  };

  return (
    <Link href={getNotificationLink()} onClick={markAsRead}>
      <div
        className={cn(
          'flex items-start justify-between p-4',
          !isRead && 'bg-gray-50',
          isFirst && 'rounded-t-lg',
          isLast && 'rounded-b-lg'
        )}
      >
        <div className="flex flex-col gap-2 flex-1">
          <div className="justify-between flex items-center">
            {notification.notificationType === 'MENTION' ? (
              <span className="text-ihs-blue text-xs">Mentions</span>
            ) : (
              <span className="text-ihs-blue text-xs">Comment</span>
            )}

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger onClick={(e) => e.stopPropagation()}>
                  <Ellipsis className="w-4 h-4 text-gray-500" />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={handleChangeReadStatus}>
                    <span className="text-sm">
                      {isRead ? 'Mark as unread' : 'Mark as read'}
                    </span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <span className="font-semibold">
                {notification.sender?.fullName}
              </span>
              <span className="text-gray-600">{getNotificationText()}</span>
              {notification.story.title && (
                <span className="font-semibold">
                  '{notification.story.title}'
                </span>
              )}
            </div>
            <span className="text-sm text-gray-500">
              {weeksDifference >= 1
                ? format(createdAtDate, 'MMM d, yyyy')
                : formatDistanceToNow(createdAtDate, { addSuffix: true })}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
